import QRCode from 'qrcode';

/** Lo mínimo de un activo para armar su etiqueta de QR. */
export interface ActivoEtiquetaQr {
  codigoQr: string;
  numero: number;
  nombre: string;
  /** Sede o ubicación donde está: ayuda a encontrar el equipo al escanear la etiqueta. */
  ubicacionNombre?: string | null;
}

const ANCHO = 600;
const ALTO = 640;
const LADO_QR = 440;
const MARGEN_SUPERIOR = 40;

/** Recorta el texto con «…» hasta que entre en el ancho disponible. */
function recortar(ctx: CanvasRenderingContext2D, texto: string, maximo: number): string {
  if (ctx.measureText(texto).width <= maximo) return texto;
  let corte = texto;
  while (corte.length > 1 && ctx.measureText(`${corte}…`).width > maximo) {
    corte = corte.slice(0, -1);
  }
  return `${corte}…`;
}

/**
 * Etiqueta de un activo como imagen PNG: el QR con su número, nombre, ubicación y
 * código debajo, para imprimir y pegar en el equipo. Se arma en el cliente; no se
 * guarda nada en el servidor.
 */
export async function etiquetaQrPng(activo: ActivoEtiquetaQr): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = ANCHO;
  canvas.height = ALTO;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no permite generar la etiqueta.');

  const qr = document.createElement('canvas');
  await QRCode.toCanvas(qr, activo.codigoQr, { width: LADO_QR, margin: 1 });

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, ANCHO, ALTO);
  ctx.drawImage(qr, (ANCHO - LADO_QR) / 2, MARGEN_SUPERIOR);

  const yTexto = MARGEN_SUPERIOR + LADO_QR + 56;
  ctx.fillStyle = '#111111';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 40px system-ui, sans-serif';
  ctx.fillText(recortar(ctx, `#${activo.numero}  ${activo.nombre}`, ANCHO - 40), ANCHO / 2, yTexto);

  if (activo.ubicacionNombre) {
    ctx.font = '28px system-ui, sans-serif';
    ctx.fillStyle = '#444444';
    ctx.fillText(recortar(ctx, activo.ubicacionNombre, ANCHO - 40), ANCHO / 2, yTexto + 50);
  }

  return canvas.toDataURL('image/png');
}

/** Carga una imagen data-URL y espera a que esté lista para dibujarla. */
function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolver, rechazar) => {
    const img = new Image();
    img.onload = () => resolver(img);
    img.onerror = () => rechazar(new Error('No se pudo preparar una etiqueta.'));
    img.src = src;
  });
}

/**
 * Todas las etiquetas en una sola imagen, en dos columnas. Es lo que se muestra en el celular, donde
 * no se puede imprimir ni bajar varios archivos.
 */
export async function hojaQrPng(activos: ActivoEtiquetaQr[]): Promise<string> {
  const imagenes = await Promise.all(activos.map((a) => etiquetaQrPng(a).then(cargarImagen)));
  if (imagenes.length === 0) throw new Error('No hay códigos QR para generar.');
  const columnas = Math.min(2, imagenes.length);
  const filas = Math.ceil(imagenes.length / columnas);
  const { width: ancho, height: alto } = imagenes[0]!;
  const canvas = document.createElement('canvas');
  canvas.width = columnas * ancho;
  canvas.height = filas * alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('El navegador no permite generar la imagen.');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  imagenes.forEach((img, i) => ctx.drawImage(img, (i % columnas) * ancho, Math.floor(i / columnas) * alto));
  return canvas.toDataURL('image/png');
}

/** Nombre de archivo sin caracteres que los sistemas operativos no admiten. */
function nombreArchivo(activo: ActivoEtiquetaQr): string {
  const nombre = activo.nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return `qr-activo-${activo.numero}${nombre ? `-${nombre}` : ''}.png`;
}

/** Descarga la etiqueta de un activo como PNG. */
export async function descargarQrActivo(activo: ActivoEtiquetaQr): Promise<void> {
  const url = await etiquetaQrPng(activo);
  const enlace = document.createElement('a');
  enlace.href = url;
  enlace.download = nombreArchivo(activo);
  document.body.appendChild(enlace);
  enlace.click();
  enlace.remove();
}

function escapar(texto: string): string {
  return texto.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/**
 * Hoja imprimible con las etiquetas de varios activos, en cuadrícula. Se abre en una
 * ventana nueva y el diálogo de impresión permite guardarla como PDF.
 *
 * La ventana se abre antes de generar las imágenes: si se abriera después, el
 * navegador la bloquearía por no venir de un clic directo.
 */
export async function imprimirHojaQr(activos: ActivoEtiquetaQr[]): Promise<void> {
  const ventana = window.open('', '_blank');
  if (!ventana) throw new Error('El navegador bloqueó la ventana. Permita las ventanas emergentes para imprimir los QR.');

  ventana.document.title = 'Etiquetas QR';
  ventana.document.body.innerHTML = '<p style="font-family:system-ui,sans-serif">Generando etiquetas…</p>';

  // Si una etiqueta falla, se cierra la ventana para no dejar «Generando…» y el error sigue hasta el llamador.
  const imagenes = await Promise.all(activos.map(etiquetaQrPng)).catch((error: unknown) => {
    ventana.close();
    throw error;
  });
  const celdas = imagenes
    .map((src, i) => `<figure><img src="${src}" alt="Etiqueta ${escapar(activos[i]!.codigoQr)}"></figure>`)
    .join('');

  ventana.document.head.innerHTML = `<meta charset="utf-8"><style>
    /* Margen 0 en la página: el navegador no imprime su encabezado (fecha, URL) en ese caso. */
    @page { margin: 0; }
    body { margin: 0; padding: 10mm; font-family: system-ui, sans-serif; }
    .hoja { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6mm; }
    figure { margin: 0; break-inside: avoid; }
    img { width: 100%; height: auto; display: block; }
  </style>`;
  ventana.document.body.innerHTML = `<div class="hoja">${celdas}</div>`;
  // El diálogo de impresión no debe abrirse antes de que las imágenes estén cargadas.
  await Promise.all(
    Array.from(ventana.document.images).map(
      (img) =>
        new Promise<void>((resolver) => {
          if (img.complete) return resolver();
          img.addEventListener('load', () => resolver(), { once: true });
          img.addEventListener('error', () => resolver(), { once: true });
        }),
    ),
  );
  ventana.focus();
  ventana.print();
}
